import React, { useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { dayStr } from "./settingsLogic";

// ✕ Elimina con conferma in linea: il primo clic mostra "Sicuro?", il secondo elimina.
export const ConfirmDelete = ({ onConfirm, label = "✕", className = "" }) => {
  const { t } = useTranslation("bookings");
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);
  return armed ? (
    <Button size="sm" color="danger" className={`py-0 px-2 ${className}`} onClick={() => { setArmed(false); onConfirm(); }}>
      {t("settings.sure")}
    </Button>
  ) : (
    <Button size="sm" color="link" className={`text-danger ${className}`} title={t("settings.delete")} onClick={() => setArmed(true)}>{label}</Button>
  );
};

// Editor di una fascia: caselle Lun...Dom, dalle / alle, (coperti), Salva / Cancella.
const RowEditor = ({ row, withCap, warn, onSave, onCancel }) => {
  const { t } = useTranslation("bookings");
  const DAYS = t("days", { returnObjects: true });
  const [r, setR] = useState(row);
  const [busy, setBusy] = useState(false);
  const toggle = (d) => setR({ ...r, days: r.days.includes(d) ? r.days.filter((x) => x !== d) : [...r.days, d].sort((a, b) => a - b) });
  const capOk = !withCap || Number(r.capacity) >= 1;
  const valid = r.days.length > 0 && r.from && r.to && r.to > r.from && capOk;
  const save = async () => {
    setBusy(true);
    const ok = await onSave(r);
    if (!ok) setBusy(false);
  };
  return (
    <div className="border rounded p-3 mb-2" style={{ background: "rgba(184,147,47,0.05)" }}>
      <div className="mb-2 d-flex flex-wrap">
        {DAYS.map((n, i) => (
          <label key={n} className="mr-3 mb-1"><input type="checkbox" checked={r.days.includes(i)} onChange={() => toggle(i)} /> {n}</label>
        ))}
      </div>
      <div className="d-flex align-items-end flex-wrap">
        <div className="mr-2 mb-2">
          <Label className="small mb-0">{t("settings.from")}</Label>
          <Input type="time" value={r.from} onChange={(e) => setR({ ...r, from: e.target.value })} style={{ maxWidth: 130 }} />
        </div>
        <div className="mr-3 mb-2">
          <Label className="small mb-0">{t("settings.to")}</Label>
          <Input type="time" value={r.to} onChange={(e) => setR({ ...r, to: e.target.value })} style={{ maxWidth: 130 }} />
        </div>
        {withCap && (
          <div className="mr-3 mb-2">
            <Label className="small mb-0">{t("settings.capacity")}</Label>
            <Input type="number" min="1" value={r.capacity} onChange={(e) => setR({ ...r, capacity: e.target.value })} style={{ maxWidth: 130 }} />
          </div>
        )}
        <div className="mb-2">
          <Button size="sm" color="primary" disabled={!valid || busy} onClick={save}>{t("save")}</Button>
          <Button size="sm" color="link" onClick={onCancel}>{t("settings.discard")}</Button>
        </div>
      </div>
      {!r.days.length && <small className="text-danger d-block">{t("settings.pickDay")}</small>}
      {r.from && r.to && r.to <= r.from && <small className="text-danger d-block">{t("settings.toAfterFrom")}</small>}
      {valid && warn && warn(r) && <small className="text-warning d-block">{t("settings.outsideOpening")}</small>}
    </div>
  );
};

// Elenco di fasce {days, from, to, capacity?} con ✎ Modifica e ✕ Elimina.
// onSave(item|null, row) e onDelete(item) restituiscono true se l'operazione è riuscita.
const HoursEditor = ({ items, withCap, empty, addLabel, newRow, title, warn, onSave, onDelete }) => {
  const { t } = useTranslation("bookings");
  const FULL = t("settings.daysFull", { returnObjects: true });
  const [edit, setEdit] = useState(null); // chiave della fascia, "new" = nuova
  const save = async (item, row) => {
    const ok = await onSave(item, row);
    if (ok) setEdit(null);
    return ok;
  };
  return (
    <>
      {items.length === 0 && edit !== "new" && <p className="text-muted small">{empty}</p>}
      {items.map((it) =>
        edit === it.key ? (
          <RowEditor key={it.key} row={it} withCap={withCap} warn={warn} onCancel={() => setEdit(null)} onSave={(v) => save(it, v)} />
        ) : (
          <div key={it.key} className="d-flex align-items-center flex-wrap py-2 border-bottom">
            <div className="flex-grow-1">
              {title && <strong className="mr-2">{title(it)}</strong>}
              <span>{dayStr(it.days, FULL)}: </span>
              <strong>{it.from} - {it.to}</strong>
              {withCap && <span className="text-muted ml-2">· {t("settings.covers", { n: it.capacity })}</span>}
              {it.badge}
              {warn && warn(it) && <small className="text-warning d-block">{t("settings.outsideOpening")}</small>}
            </div>
            <Button size="sm" color="link" title={t("settings.edit")} onClick={() => setEdit(it.key)}>✎</Button>
            <ConfirmDelete onConfirm={() => onDelete(it)} />
          </div>
        )
      )}
      {edit === "new" ? (
        <RowEditor row={newRow} withCap={withCap} warn={warn} onCancel={() => setEdit(null)} onSave={(v) => save(null, v)} />
      ) : (
        <Button size="sm" color="link" className="px-0 mt-2" onClick={() => setEdit("new")}>+ {addLabel}</Button>
      )}
    </>
  );
};

export default HoursEditor;
